import { reaction } from 'mobx';
import { useEffect, useState } from 'react';

import { company } from '~/company/Company';
import { intercomAuth } from '~/services/IntercomAuth';
import { userStore } from '~/stores';

interface IntercomSettings {
  app_id: string;
  name?: string;
  email?: string;
  phone?: string;
  anonymous_id?: string;
  user_id?: string | number;
  intercom_user_jwt?: string;
  [key: string]: any;
}

export type IntercomType = {
  (command: 'boot', settings: IntercomSettings): void;
  (command: 'shutdown' | 'hide' | 'show' | 'showMessages'): void;
  (command: 'update', settings?: IntercomSettings): void;
  (command: 'showNewMessage', content?: string): void;
  (...args: any[]): void;
};

declare global {
  interface Window {
    Intercom: IntercomType;
  }
}

const bootIntercomWithUserData = async () => {
  if (!window.Intercom) return;

  const token = await intercomAuth.getFreshToken();
  if (!token) return;

  window.Intercom('boot', {
    app_id: company.envVariables.REACT_APP_INTERCOM_ID,
    user_id: userStore.personalData.id,
    intercom_user_jwt: token,
    name: userStore.fullName || undefined,
    company: {
      company_id: company.config.id,
      name: company.config.name,
    },
  });
};

if (typeof window !== 'undefined') {
  reaction(
    () => userStore.isAuthorized,
    (isAuthorized) => {
      if (isAuthorized) {
        bootIntercomWithUserData();
      } else {
        intercomAuth.invalidate();
        if (window.Intercom) {
          window.Intercom('shutdown');
        }
      }
    },
    { fireImmediately: true },
  );
}

export const useIntercom = () => {
  const [isOpening, setIsOpening] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const openMessenger = async () => {
    if (!window.Intercom) return;

    setIsOpening(true);

    const token = userStore.isAuthorized
      ? await intercomAuth.getFreshToken()
      : null;

    window.Intercom('showMessages');
    if (userStore.isAuthorized) {
      window.Intercom('update', {
        name: userStore.fullName,
        ...(token ? { intercom_user_jwt: token } : {}),
      });
    }
  };

  const updateIntercom = async (settings: Omit<IntercomSettings, 'app_id'>) => {
    if (!window.Intercom) return;

    const token = userStore.isAuthorized
      ? await intercomAuth.getFreshToken()
      : null;

    window.Intercom('update', {
      ...(token ? { intercom_user_jwt: token } : {}),
      ...settings,
    });
  };

  const shutdownIntercom = () => {
    if (window.Intercom) {
      window.Intercom('shutdown');
    }
  };

  useEffect(() => {
    window.Intercom('onShow', function () {
      setIsOpening(false);
      setIsOpen(true);
    });
    window.Intercom('onHide', function () {
      setIsOpen(false);
    });
  }, []);

  return {
    openMessenger,
    updateIntercom,
    shutdownIntercom,
    isOpening,
    isOpen,
  };
};
